import { useState, useMemo, useCallback, useEffect } from "react";
import {
  Box,
  Pagination,
  TableContainer,
  Table,
  TableCell,
  TableHead,
  TableRow,
  TableBody,
  TableSortLabel,
  Checkbox,
  Typography,
  CircularProgress,
} from "@mui/material";
import SearchBarContainer from "./SearchBarContainer";
import { makeStyles, useTheme } from "@mui/styles";
import Header from "./Header";

const useStyles = makeStyles((theme) => ({
  tableCell: {
    backgroundColor: "transparent",
    fontSize: "12px !important",
    color: `${theme.palette.text.color} !important`,
    height: "10px",
    padding: "6px 4px !important",
    borderBottom: `1px solid ${theme.palette.border.color} !important`,
  },
  headerCell: {
    backgroundColor: theme.palette.tableHeader.color,
    borderBottom: "none !important",
    padding: "6px 4px !important",
    fontSize: "14px",
    color: `${theme.palette.text.color} !important`,
    height: "10px",
  },
}));

const RenderColumnItem = (column, row, object, handleUpdateAnnotation) => {
  if (column.tableRenderType === "toggleButton") {
    const currentStatus = row.annotationStatus;
    const isAddedToWMS = currentStatus === "ADD TO WMS";
    const buttonText = isAddedToWMS ? "Remove from WMS" : "Add to WMS";
    const buttonColor = isAddedToWMS ? "#dc3545" : "#006bce";

    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          const newStatus = isAddedToWMS ? "REMOVE FROM WMS" : "ADD TO WMS";
          handleUpdateAnnotation({ ...row, annotationStatus: newStatus });
        }}
        style={{
          backgroundColor: buttonColor,
          border: `1px solid ${buttonColor}`,
          color: "#fff",
          fontSize: "12px",
          cursor: "pointer",
          padding: "4px 8px",
          borderRadius: "4px",
          transition: "all 0.2s ease-in-out",
          minWidth: "120px",
          textAlign: "center",
          display: "block",
          margin: "0 auto",
        }}
      >
        {buttonText}
      </button>
    );
  }

  switch (column.type) {
    case "textfield":
      return (
        <Typography
          sx={{
            fontSize: "14px !important",
          }}
        >
          {column.value}
        </Typography>
      );
    case "description":
      return (
        <Typography
          sx={{
            fontSize: "14px !important",
          }}
        >
          {column.value}
        </Typography>
      );
    case "checkbox":
      return (
        <Checkbox
          color="primary"
          indeterminate={column.indeterminate}
          checked={column.checked}
          onChange={column.onChange}
          sx={{
            color: "#f5f5f5",
            "& .MuiSvgIcon-root": {
              fontSize: "14px !important",
            },
            "&.Mui-checked": { color: "#006bce" },
            "&.MuiCheckbox-indeterminate": { color: "#006bce" },
          }}
        />
      );
    case "icon":
      return (
        <column.icon
          sx={{
            color: "#fff",
            fontSize: "14px",
            "&:hover": {
              color: "#fff",
            },
          }}
        />
      );
    case "button":
      return (
        <button
          onClick={column.onClick}
          style={{
            backgroundColor: "transparent",
            border: "none",
            color: "#fff",
            fontSize: "14px",
            cursor: "pointer",
            padding: "0",
            margin: "0",
            "&:hover": {
              color: "#fff",
            },
          }}
        >
          {column.label}
        </button>
      );
    case "link":
      return (
        <a
          href
          onClick={column.onClick}
          style={{
            color: "#fff",
            fontSize: "14px",
            textDecoration: "none",
            cursor: "pointer",
            "&:hover": {
              textDecoration: "underline",
            },
          }}
        >
          {column.label}
        </a>
      );
    case "image":
      return (
        <img
          src={column.src}
          alt={column.alt}
          style={{
            width: column.width || "50px",
            height: column.height || "50px",
            objectFit: column.objectFit || "cover",
            borderRadius: column.borderRadius || "4px",
            cursor: column.cursor || "pointer",
            "&:hover": {
              transform: column.transform || "scale(1.05)",
            },
          }}
        />
      );
    case "dropdown":
      return (
        <Typography
          sx={{
            fontSize: "14px !important",
          }}
        >
          {column.value}
        </Typography>
      );

    case "tableDropdown":
      const currentStatus = row.annotationStatus;
      const isAddedToWMS = currentStatus === "ADD TO WMS";
      const buttonText = isAddedToWMS ? "REMOVE FROM WMS" : "ADD TO WMS";
      const buttonColor = isAddedToWMS ? "#dc3545" : "#006bce";

      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            const newStatus = isAddedToWMS ? "REMOVE FROM WMS" : "ADD TO WMS";
            handleUpdateAnnotation({ ...row, annotationStatus: newStatus });
          }}
          style={{
            backgroundColor: buttonColor,
            border: `1px solid ${buttonColor}`,
            color: "#fff",
            fontSize: "12px",
            cursor: "pointer",
            padding: "4px 8px",
            borderRadius: "4px",
            transition: "all 0.2s ease-in-out",
            minWidth: "120px",
            textAlign: "center",
            display: "block",
            margin: "0 auto",
          }}
        >
          {buttonText}
        </button>
      );

    case "date":
      const timestamp = Number(column.value);
      const digitLength = String(timestamp)?.length;
      const adjustedTimestamp =
        digitLength <= 10 ? timestamp * 1000 : timestamp;
      const date = new Date(adjustedTimestamp);
      return (
        <Typography
          style={{
            fontSize: "14px",
          }}
        >
          {isNaN(date.getTime())
            ? column.value || "Invalid date"
            : date.toLocaleDateString("en-GB", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            })}
        </Typography>
      );
    case "geoJson":
      const latitude = row[`${object}Latitude`];
      const longitude = row[`${object}Longitude`];
      return (
        <Typography
          component="a"
          // href={`https://www.google.com/maps?q=${row.locationLatitude},${row.locationLongitude}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            textDecoration: "none",
            fontSize: "14px !important",
            "&:hover": {
              textDecoration: "underline",
            },
          }}
        >
          {latitude && longitude ? `${latitude}, ${longitude}` : "N/A"}
        </Typography>
      );
    default:
      return null;
  }
};

// Sorting utility functions
const getComparator = (order, orderBy) => {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

const descendingComparator = (a, b, orderBy) => {
  const aValue = a[orderBy];
  const bValue = b[orderBy];
  if (bValue < aValue) return -1;
  if (bValue > aValue) return 1;
  return 0;
};

const stableSort = (array, comparator) => {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
};

const ListComponent = (props) => {
  const {
    header = true,
    Icon,
    objectDefinition,
    title,
    showAddButton,
    addButtonText,
    showOtherButton,
    otherButtonText,
    setShowAddForm,
    showActions,
    actionOptions,
    selectedAction,
    onAddClick,
    onOtherClick,
    onDeleteClick,
    handleUpdateAnnotation,
    selectedObject,
    handleChange,
    onRefresh,
    object,
    columns,
    data,
    selectedRows,
    setSelectedRows,
    setShowUpdateForm,
    isLoading,
    isPropertyLoading,
    sortingOrder,
    sortBy,
    rowClick = false,
  } = props;
  const theme = useTheme();
  const rowCount = data?.length;
  const numSelected = selectedRows?.length;
  const classes = useStyles();

  const [page, setPage] = useState(1);
  const rowsPerPage = 20;
  const totalPages = Math.ceil(data?.length / rowsPerPage);

  const [tableData, setTableData] = useState(
    data?.slice((page - 1) * rowsPerPage, page * rowsPerPage)
  );

  // Popover state
  const [anchorElOptions, setAnchorElOptions] = useState(null);
  const [anchorElAttributes, setAnchorElAttributes] = useState(null);
  const [anchorElFilter, setAnchorElFilter] = useState(null);
  const [anchorElStatus, setAnchorElStatus] = useState(null);
  const [anchorElType, setAnchorElType] = useState(null);
  const openOptions = Boolean(anchorElOptions);
  const openAttributes = Boolean(anchorElAttributes);
  const openFilter = Boolean(anchorElFilter);
  const openStatus = Boolean(anchorElStatus);
  const openType = Boolean(anchorElType);

  //filter state
  const [filteredStatus, setFilteredStatus] = useState(null);
  const [filteredType, setFilteredType] = useState(null);

  //sort state
  const [order, setOrder] = useState(sortingOrder ? sortingOrder : "asc");
  const [orderBy, setOrderBy] = useState(
    sortBy ? sortBy : columns?.[0]?.id || ""
  );

  // Column visibility state
  const [visibilityStatus, setVisibilityStatus] = useState(
    columns?.reduce((acc, column) => {
      acc[column.id] = true; // Initialize all columns as visible
      return acc;
    }, {})
  );

  // Check if a row is selected by comparing its ID
  const isSelected = (row) => {
    const selectedIndex = selectedRows?.findIndex(
      (selected) => selected[`${object}Id`] === row[`${object}Id`]
    );
    const isSelected = selectedIndex !== -1;
    return isSelected;
  };
  //return true if the row is selected

  // handle form add ctate
  const handleAddClick = () => {
    // Debug
    if (objectDefinition) {
      props?.setFormObject?.(objectDefinition);
    } else {
      props?.setFormObject(null);
      console.warn("objectDefinition is undefined");
    }
    setShowAddForm(true);
  };

  // Handle form modify state
  const handleModifyClick = () => {
    setShowUpdateForm(true);
  };

  // Handle Popover open/close
  const handleOptionsClick = (event) => {
    setAnchorElOptions(event.currentTarget);
  };

  const handleOptionsClose = () => {
    setAnchorElOptions(null);
    setAnchorElAttributes(null);
  };

  //handle attributes
  const handleAttributesClick = (event) => {
    setAnchorElAttributes(anchorElOptions);
    setAnchorElOptions(null);
  };

  const handleAttributesClose = () => {
    setAnchorElOptions(anchorElAttributes);
    setAnchorElAttributes(null);
  };

  const handleSettingsClick = () => {
    // Implement settings functionality
    handleOptionsClose();
  };

  // Handle column visibility toggle
  const handleToggle = (columnId) => (event) => {
    setVisibilityStatus((prev) => ({
      ...prev,
      [columnId]: event.target.checked,
    }));
  };

  //handle filter
  const handleFilterClick = useCallback((event) => {
    setAnchorElFilter(event.currentTarget);
  }, []);

  const handleFilterClose = useCallback(() => {
    setAnchorElFilter(null);
  }, []);

  const handleStatusClick = () => {
    setAnchorElStatus(anchorElFilter);
    setAnchorElFilter(null);
  };

  const handleStatusClose = () => {
    setAnchorElFilter(anchorElStatus);
    setAnchorElStatus(null);
  };

  const handleTypeClick = () => {
    setAnchorElType(anchorElFilter);
    setAnchorElFilter(null);
  };

  const handleTypeClose = () => {
    setAnchorElFilter(anchorElType);
    setAnchorElType(null);
  };

  // Select all rows
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      setSelectedRows([...data]);
      return;
    }
    setSelectedRows([]);
  };

  // Toggle selection of a single row
  const handleSelected = (event, row, rowIndex) => {
    const selectedIndex = selectedRows?.findIndex(
      (selected) => selected[`${object}Id`] === row[`${object}Id`]
    );
    let newSelectedRows = [...selectedRows];

    if (selectedIndex === -1) {
      newSelectedRows.push(row);
    } else {
      newSelectedRows.splice(selectedIndex, 1);
    }
    setSelectedRows(newSelectedRows);
    if (newSelectedRows?.length > 0) {
      props?.setFormObject?.(newSelectedRows[0]);
    }
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Filter visible columns based on visibilityStatus
  // const visibleColumns = columns?.filter(
  //   (column) => visibilityStatus[column.id]
  // );
  const onSearch = (searchValue) => {
    const filteredData = data.filter((row) =>
      Object.values(row).some((value) =>
        String(value).toLowerCase().includes(searchValue.toLowerCase())
      )
    );
    setTableData(filteredData);
  };

  const handleSort = useCallback(
    (property) => {
      const isAsc = orderBy === property && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(property);
    },
    [order, orderBy]
  );

  // Sorted data
  const sortedData = useMemo(() => {
    return stableSort(data || [], getComparator(order, orderBy));
  }, [data, order, orderBy]);

  const filteredData = useMemo(() => {
    return tableData?.filter((row) => {
      if (!filteredStatus && !filteredType) return true;

      const statusMatch = filteredStatus
        ? row.projectStatus === filteredStatus
        : true;

      const typeMatch = filteredType ? row.projectType === filteredType : true;

      return statusMatch && typeMatch;
    });
  }, [tableData, filteredStatus, filteredType]);

  useEffect(() => {
    if (data && data?.length > 0) {
      const paginatedData = sortedData?.slice(
        (page - 1) * rowsPerPage,
        page * rowsPerPage
      );
      setTableData(paginatedData);
    } else if (data && data?.length === 0) {
    }
  }, [data, sortedData, page, rowsPerPage]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        width: "100%",
        gap: header && 2,
      }}
    >
      <Header
        Icon={Icon}
        title={title}
        subtitle="Register and monitor your hardware · Track device status across your fleet"
        showAddButton={showAddButton}
        addButtonText={addButtonText}
        showOtherButton={showOtherButton}
        otherButtonText={otherButtonText}
        onAddClick={onAddClick ? onAddClick : handleAddClick}
        onOtherClick={onOtherClick}
        showActions={showActions}
        actionOptions={actionOptions}
        selectedAction={selectedAction}
        onDeleteClick={onDeleteClick}
        handleFilterClick={handleFilterClick}
        handleFilterClose={handleFilterClose}
        openFilter={openFilter}
        openStatus={openStatus}
        openType={openType}
        anchorElFilter={anchorElFilter}
        anchorElStatus={anchorElStatus}
        anchorElType={anchorElType}
        filteredStatus={filteredStatus}
        setFilteredStatus={setFilteredStatus}
        filteredType={filteredType}
        setFilteredType={setFilteredType}
        onRefresh={onRefresh}
        columns={columns}
        openOptions={openOptions}
        anchorElOptions={anchorElOptions}
        handleOptionsClick={handleOptionsClick}
        handleOptionsClose={handleOptionsClose}
        handleAttributesClick={handleAttributesClick}
        handleSettingsClick={handleSettingsClick}
        openAttributes={openAttributes}
        anchorElAttributes={anchorElAttributes}
        handleAttributesClose={handleAttributesClose}
        handleStatusClick={handleStatusClick}
        handleStatusClose={handleStatusClose}
        handleTypeClick={handleTypeClick}
        handleTypeClose={handleTypeClose}
        visibilityStatus={visibilityStatus}
        handleToggle={handleToggle}
        selectedRows={selectedRows}
        onModifyClick={handleModifyClick}
        object={object}
        selectedObject={selectedObject}
        handleChange={handleChange}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            backgroundColor: "transparent",
            padding: "18px 12px",
            boxSizing: "border-box",
          }}
        >
          <SearchBarContainer onSearch={onSearch} />
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Pagination
              count={totalPages || 0}
              page={page}
              onChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              sx={{
                "& .MuiPaginationItem-root": {
                  color: theme.palette.text.color,
                  border: "none",
                  minWidth: 24,
                  height: 24,
                  fontSize: "0.75rem",
                  borderRadius: 0,
                },
                "& .Mui-selected": {
                  backgroundColor: "transparent !important",
                  border: "none",
                  borderBottom: "1px solid #006BCE",
                  color: "#006BCE",
                },
                "& .MuiPaginationItem-previousNext": {
                  border: `1px solid ${theme.palette.border.color}`,
                  borderRadius: "50% !important",
                },
              }}
            />
          </Box>
        </Box>

        {!isPropertyLoading ? (
            <TableContainer
              sx={{
                width: "-webkit-fill-available",
                boxSizing: "border-box",
                backgroundColor: theme.palette.primary.main,
                margin: 1,
                borderRadius: "16px",
                border: `1px solid ${theme.palette.border.color}`,
                maxHeight: "100%",
                overflow: "auto",
                "&::-webkit-scrollbar": {
                  width: "6px",
                  height: "6px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: theme.palette.border.color,
                  borderRadius: "3px",
                },
              }}
            >
              <Table
                stickyHeader
                aria-label="sticky table"
                sx={{
                  paddingTop: 0,
                  backgroundColor: theme.palette.primary.color,
                  p: 1,
                  borderRadius: 2,
                  boxSizing: "border-box",
                }}
              >
                <TableHead
                  sx={{
                    "& .MuiTableRow-root": {
                      "& .MuiTableCell-root": {
                        backgroundColor: theme.palette.tableHeader.color,
                        fontWeight: "bold",
                      },
                      "& .MuiTableCell-root:first-of-type": {
                        borderTopLeftRadius: "22px",
                        borderBottomLeftRadius: "22px",
                      },
                      "& .MuiTableCell-root:last-of-type": {
                        borderTopRightRadius: "22px",
                        borderBottomRightRadius: "22px",
                      },
                    },
                  }}
                >
                  <TableRow>
                    <TableCell
                      align="center"
                      padding="checkbox"
                      className={classes.headerCell}
                    >
                      {columns?.length > 0 && (
                        <Checkbox
                          color="primary"
                          indeterminate={
                            numSelected > 0 && numSelected < rowCount
                          }
                          checked={rowCount > 0 && numSelected === rowCount}
                          onChange={handleSelectAllClick}
                          sx={{
                            color: theme.palette.text.color,
                            "& .MuiSvgIcon-root": {
                              fontSize: "14px !important",
                            },
                            "&.Mui-checked": { color: "#006bce" },
                            "&.MuiCheckbox-indeterminate": { color: "#006bce" },
                          }}
                        />
                      )}
                    </TableCell>
                    {columns?.map((column) => (
                      <>
                        <TableCell
                          key={column.id}
                          align="left"
                          className={classes.headerCell}
                        >
                          <TableSortLabel
                            active={orderBy === column.id}
                            direction={orderBy === column.id ? order : "asc"}
                            onClick={() => handleSort(column.id)}
                            sx={{
                              color: `${theme.palette.text.color} !important`,
                              fontSize: "14px",
                              "&:hover": {
                                color: `${theme.palette.text.color} !important`,
                              },
                              "&.Mui-active": {
                                color: `${theme.palette.text.color} !important`,
                              },
                              "& .MuiTableSortLabel-icon": {
                                color: `${theme.palette.text.color} !important`,
                              },
                            }}
                          >
                            {column.label}
                          </TableSortLabel>
                        </TableCell>
                      </>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!isLoading ?
                    filteredData?.map((row, rowIndex) => {
                      const isItemSelected = isSelected(
                        row,
                        rowIndex,
                        page,
                        rowsPerPage
                      );
                      const labelId = `enhanced-table-checkbox-${rowIndex}`;
                      return (
                        <TableRow
                          hover
                          role="checkbox"
                          tabIndex={-1}
                          key={row[`${object}Id`]}
                          sx={{
                            "&:last-child td, &:last-child th": {
                              border: 0,
                            },
                            backgroundColor: "transparent",
                            "&:hover": {
                              backgroundColor: "transparent",
                            },
                            "&:active": {
                              backgroundColor: "transparent",
                            },
                          }}
                        >
                          <TableCell
                            align="center"
                            padding="checkbox"
                            className={classes.tableCell}
                          >
                            {columns?.length > 0 && (
                              <Checkbox
                                sx={{
                                  color: theme.palette.text.color,
                                  "&.Mui-checked": { color: "#006bce" },
                                  "& .MuiSvgIcon-root": {
                                    fontSize: "14px !important",
                                  },
                                }}
                                checked={isItemSelected}
                                onClick={(event) =>
                                  handleSelected(event, row, rowIndex)
                                }
                                inputProps={{ "aria-labelledby": labelId }}
                              />
                            )}
                          </TableCell>
                          {columns?.map((column) => (
                            <>
                              <TableCell
                                key={column.id}
                                align={column.align}
                                className={classes.tableCell}
                                sx={{
                                  cursor:
                                    column.onClick || rowClick
                                      ? "pointer"
                                      : "default",
                                  color: theme.palette.text.color,
                                }}
                                onClick={() => {
                                  column.onClick && column.onClick(row);
                                  if (rowClick) {
                                    setShowUpdateForm(true);
                                    props?.setFormObject(row);
                                  }
                                }}
                              >
                                {RenderColumnItem(
                                  {
                                    ...column,
                                    value: row[column.id],
                                    coordinates: row?.imageGeoHash?.coordinates,
                                  },
                                  row,
                                  object,
                                  handleUpdateAnnotation
                                ) || row[column.id]}
                              </TableCell>
                            </>
                          ))}
                        </TableRow>
                      );
                    }) : <CircularProgress
                      sx={{
                        color: theme.palette.text.color,
                        my: 2
                      }}
                    />
                  }
                </TableBody>
              </Table>
            </TableContainer>
          ) 
         :  <CircularProgress 
            sx={{
              color: theme.palette.text.color,
              my: 2
            }}
          />}
      </Box>
    </Box>
  );
};

export default ListComponent;
